
import { useCustomer } from './hooks/useCustomer';

export default function CustomerDashboard() {
  const { profile, orders, warranty } = useCustomer();
  const p = profile();
  const o = orders();
  const w = warranty();

  if (p.isLoading || o.isLoading || w.isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  const activeOrders = o.data?.filter((x: any) => x.status !== 'delivered') || [];
  const activeWarranties = w.data?.filter((x: any) => new Date(x.expiryDate) > new Date()) || [];

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-amber-50 to-orange-50 min-h-screen">
      {/* Welcome Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-amber-600">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Welcome back, {p.data?.name}!
        </h1>
        <p className="text-gray-600">
          Manage your orders, track warranties, and explore our latest collections.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Loyalty Points</h3>
              <p className="text-2xl font-bold text-amber-600">{p.data?.loyaltyPoints || 0}</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <i className="ri-star-fill text-amber-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
              <p className="text-2xl font-bold text-blue-600">{o.data?.length || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-shopping-bag-3-fill text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Active Orders</h3>
              <p className="text-2xl font-bold text-green-600">{activeOrders.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-truck-fill text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Active Warranties</h3>
              <p className="text-2xl font-bold text-purple-600">{activeWarranties.length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className="ri-shield-check-fill text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Recent Orders</h2>
          <a href="/customer/orders" className="text-amber-600 hover:text-amber-700 font-medium">
            View All Orders →
          </a>
        </div>
        
        {o.data && o.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Order No</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Total</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Delivery Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {o.data.slice(0, 5).map((order: any) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{order.orderNo}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">₹{order.total?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-600">{order.deliveryDate}</td>
                    <td className="px-4 py-3">
                      <button className="text-amber-600 hover:text-amber-700 font-medium">
                        Track Order
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <i className="ri-shopping-bag-3-line text-4xl mb-2"></i>
            <p>No orders found. Start shopping to see your orders here!</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <a href="/customer/orders" className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <i className="ri-list-check-2 text-blue-600 text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">View All Orders</h3>
              <p className="text-sm text-gray-600">Track and manage your orders</p>
            </div>
          </div>
        </a>

        <a href="/customer/warranty" className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
              <i className="ri-shield-check-line text-purple-600 text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Warranty Claims</h3>
              <p className="text-sm text-gray-600">Manage warranty and repairs</p>
            </div>
          </div>
        </a>

        <a href="/customer/profile" className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
              <i className="ri-user-settings-line text-green-600 text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Update Profile</h3>
              <p className="text-sm text-gray-600">Manage your account settings</p>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}
